import { X } from "lucide-react";
import { AlertDialog } from "radix-ui";
import type { ReactNode } from "react";

import { Button } from "./button";

interface ConfirmDialogAction {
  label: string;
  onClick?: () => void;
}

type ConfirmDialogVariant = "primary" | "warning";

const HEADER_BAR_CLASS: Record<ConfirmDialogVariant, string> = {
  primary: "bg-primary text-primary-foreground",
  warning: "bg-warning text-warning-foreground",
};

const HEADER_TITLE_CLASS: Record<ConfirmDialogVariant, string> = {
  primary: "text-primary-foreground",
  warning: "text-warning-foreground",
};

const HEADER_BUTTON_CLASS: Record<ConfirmDialogVariant, string> = {
  primary: "text-primary-foreground/80 hover:text-primary-foreground",
  warning: "text-warning-foreground/80 hover:text-warning-foreground",
};

interface ConfirmDialogProperties {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  title: string;
  description: ReactNode;
  /** Left of the description; also switches to the bordered-footer banner layout. */
  icon?: ReactNode;
  /** Shows a "Close" text link + X icon button in the header (Cancel-equivalent). */
  closeLabel?: string;
  maxWidthClassName?: string;
  cancelAction?: ConfirmDialogAction;
  confirmAction: ConfirmDialogAction;
  /** Header bar color — "warning" for CAPS Warning popups, "primary" otherwise. */
  variant?: ConfirmDialogVariant;
}

// Shared header-bar AlertDialog for every CAPS Warning-style confirm popup
// (income remove/change, pay-stub data-loss, pay-rate ratio alert). `icon`
// switches to the banner layout (icon + description row, bordered footer);
// omitting it renders the compact layout (description + buttons in one block).
function ConfirmDialog({
  open,
  onOpenChange,
  title,
  description,
  icon,
  closeLabel,
  maxWidthClassName = "max-w-sm",
  cancelAction,
  confirmAction,
  variant = "primary",
}: ConfirmDialogProperties) {
  return (
    <AlertDialog.Root open={open} onOpenChange={onOpenChange}>
      <AlertDialog.Portal>
        <AlertDialog.Overlay className="fixed inset-0 z-50 bg-black/40" />
        <AlertDialog.Content
          className={`bg-card border-border fixed left-1/2 top-1/2 z-50 w-full ${maxWidthClassName} -translate-x-1/2 -translate-y-1/2 overflow-hidden rounded-lg border shadow-lg`}
        >
          <div className={`${HEADER_BAR_CLASS[variant]} flex items-center justify-between border-b px-4 py-2`}>
            <AlertDialog.Title className={`${HEADER_TITLE_CLASS[variant]} text-sm font-semibold`}>
              {title}
            </AlertDialog.Title>
            {closeLabel && (
              <div className="flex items-center gap-2">
                <AlertDialog.Cancel asChild>
                  <button type="button" className={`${HEADER_BUTTON_CLASS[variant]} text-xs underline`}>
                    {closeLabel}
                  </button>
                </AlertDialog.Cancel>
                <AlertDialog.Cancel asChild>
                  <button type="button" className={`${HEADER_BUTTON_CLASS[variant]} p-0.5`}>
                    <X className="size-4" />
                    <span className="sr-only">{closeLabel}</span>
                  </button>
                </AlertDialog.Cancel>
              </div>
            )}
          </div>
          {icon ? (
            <>
              <div className="flex items-start gap-3 p-4">
                <div className="bg-warning flex size-8 shrink-0 items-center justify-center rounded-sm">{icon}</div>
                <AlertDialog.Description className="text-foreground text-sm">{description}</AlertDialog.Description>
              </div>
              <div className="flex justify-end gap-2 border-t px-4 py-3">
                {cancelAction && (
                  <AlertDialog.Cancel asChild>
                    <Button type="button" variant="outline" size="sm" onClick={cancelAction.onClick}>
                      {cancelAction.label}
                    </Button>
                  </AlertDialog.Cancel>
                )}
                <AlertDialog.Action asChild>
                  <Button type="button" size="sm" onClick={confirmAction.onClick}>
                    {confirmAction.label}
                  </Button>
                </AlertDialog.Action>
              </div>
            </>
          ) : (
            <div className="p-4">
              <AlertDialog.Description className="text-muted-foreground mb-4 text-sm">
                {description}
              </AlertDialog.Description>
              <div className="flex justify-end gap-2">
                {cancelAction && (
                  <AlertDialog.Cancel asChild>
                    <Button type="button" variant="outline" size="sm" onClick={cancelAction.onClick}>
                      {cancelAction.label}
                    </Button>
                  </AlertDialog.Cancel>
                )}
                <AlertDialog.Action asChild>
                  <Button type="button" variant="default" size="sm" onClick={confirmAction.onClick}>
                    {confirmAction.label}
                  </Button>
                </AlertDialog.Action>
              </div>
            </div>
          )}
        </AlertDialog.Content>
      </AlertDialog.Portal>
    </AlertDialog.Root>
  );
}

export default ConfirmDialog;
